import { useCallback, useState } from 'react';

import { useClient } from '@/utils/urql';

import { MICRO_SKILL_LAZY_LOAD_ID } from '../../../../../../../MicroSkillsTableView/adapter';
import { MicroSkillType } from '../../../../../../../MicroSkillsTableView/types';
import {
  SkillMicroSkillDetailsDocument,
  SkillMicroSkillDetailsQuery,
  SkillMicroSkillDetailsQueryVariables,
  SkillWithMicroSkillDetailsFragment,
} from '../../../../graphql/query/generated/skillMicroSkillDetails';

const useMicroSkillsDetails = (props: Pick<SkillWithMicroSkillDetailsFragment, 'childSkills'>) => {
  const { childSkills } = props;

  const client = useClient();

  const [state, setState] = useState<{
    hierarchyIdsMap: Record<string, string[] | undefined>;
    microSkillsMap: Record<string, MicroSkillType | undefined>;
    rootMicroSkillIds: string[];
  }>(() => {
    const rootMicroSkillIds: string[] = [];
    const hierarchyIdsMap: Record<string, string[] | undefined> = {};
    const microSkillsMap: Record<string, MicroSkillType | undefined> = {};

    childSkills?.forEach((childSkill) => {
      rootMicroSkillIds.push(childSkill.id);
      microSkillsMap[childSkill.id] = {
        id: childSkill.id,
        name: childSkill.name,
      };
      if (childSkill.childrenIds?.length) {
        hierarchyIdsMap[childSkill.id] = [MICRO_SKILL_LAZY_LOAD_ID];
      }
    });

    return { hierarchyIdsMap, microSkillsMap, rootMicroSkillIds };
  });

  const onFetchChildMicroSkill = useCallback(
    async (parentMicroSkill?: Pick<MicroSkillType, 'id'>) => {
      const parentMicroSkillId = parentMicroSkill?.id;
      if (!parentMicroSkillId) {
        return;
      }

      const response = await client
        .query<SkillMicroSkillDetailsQuery, SkillMicroSkillDetailsQueryVariables>(
          SkillMicroSkillDetailsDocument,
          { filters: { id: parentMicroSkillId } },
          { requestPolicy: 'network-only' },
        )
        .toPromise();
      const fetchedMicroSkill = response.data?.skill;

      if (fetchedMicroSkill) {
        setState((prevState) => {
          const childMicroSkills = fetchedMicroSkill.childSkills?.reduce<
            Record<string, MicroSkillType | undefined>
          >((accum, current) => {
            return {
              ...accum,
              [current.id]: current,
            };
          }, {});

          const childOfChildHierarchyIdsMap = fetchedMicroSkill.childSkills?.reduce<
            Record<string, string[] | undefined>
          >((accum, current) => {
            if (current.childrenIds?.length) {
              return {
                ...accum,
                [current.id]: [MICRO_SKILL_LAZY_LOAD_ID],
              };
            }
            return accum;
          }, {});

          return {
            ...prevState,
            hierarchyIdsMap: {
              ...prevState.hierarchyIdsMap,
              [parentMicroSkillId]: fetchedMicroSkill.childrenIds ?? [],
              ...childOfChildHierarchyIdsMap,
            },
            microSkillsMap: {
              ...prevState.microSkillsMap,
              ...childMicroSkills,
            },
          };
        });
      }
      //todo error handling
    },
    [client],
  );

  return {
    state,
    onFetchChildMicroSkill,
  };
};

export default useMicroSkillsDetails;
